import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react";

/** Pushed by the wlkthru Chrome extension (HCP capture + optional schedule slot from extension modal). */
export type ExtensionHcpPrefill = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  /** `YYYY-MM-DD` from extension schedule flow */
  scheduleDate?: string;
  /** `HH:mm` (24h), 15-min aligned — matches ScheduleCallModal `formData.time` */
  scheduleTime?: string;
  /** Epoch ms when extension saved schedule prefill; stale entries are ignored client-side */
  schedulePrefillAt?: number;
};

export const EXTENSION_HCP_PREFILL_MSG_TYPE = "WLKTHRU_HCP_PREFILL" as const;
export const EXTENSION_HCP_PREFILL_SOURCE = "wlkthru-extension" as const;

/** Page sessionStorage key — must match extension `PORTAL_PREFILL_SESSION_KEY`. */
export const EXTENSION_HCP_PREFILL_STORAGE_KEY = "wlkthru_hcp_prefill_v1";

export const EXTENSION_HCP_PREFILL_DOM_EVENT = "wlkthru-hcp-prefill";

const ExtensionHcpPrefillContext = createContext<ExtensionHcpPrefill | undefined>(undefined);

/**
 * True for production wlkthru or local/LAN Vite dev (https://localhost:8080, etc.).
 * Keep in sync with `isWlkthruPortalHost` in the Chrome extension content script.
 */
export function isExtensionPrefillAllowedOrigin(origin: string): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  const host = url.hostname.toLowerCase();
  if (/^(www\.)?wlkthru\.co$/i.test(host)) return true;
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  return false;
}

const SCHEDULE_PREFILL_TTL_MS = 48 * 60 * 60 * 1000;

function normalizePayload(raw: unknown): ExtensionHcpPrefill | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const p = raw as Record<string, unknown>;
  const firstName = typeof p.firstName === "string" ? p.firstName : "";
  const lastName = typeof p.lastName === "string" ? p.lastName : "";
  const phoneNumber = typeof p.phoneNumber === "string" ? p.phoneNumber : "";
  const email = typeof p.email === "string" ? p.email : "";
  if (!firstName.trim() && !phoneNumber.trim() && !email.trim()) return undefined;

  let scheduleDate = typeof p.scheduleDate === "string" ? p.scheduleDate.trim() : "";
  let scheduleTime = typeof p.scheduleTime === "string" ? p.scheduleTime.trim() : "";
  const schedulePrefillAt =
    typeof p.schedulePrefillAt === "number"
      ? p.schedulePrefillAt
      : typeof p.setAt === "number"
        ? p.setAt
        : 0;
  const scheduleFresh =
    schedulePrefillAt > 0 &&
    Date.now() - schedulePrefillAt <= SCHEDULE_PREFILL_TTL_MS &&
    /^\d{4}-\d{2}-\d{2}$/.test(scheduleDate) &&
    /^\d{1,2}:\d{2}$/.test(scheduleTime);
  if (!scheduleFresh) {
    scheduleDate = "";
    scheduleTime = "";
  }

  const out: ExtensionHcpPrefill = { firstName, lastName, phoneNumber, email };
  if (scheduleFresh) {
    const parts = scheduleTime.split(":");
    const h = parseInt(parts[0] || "", 10);
    const m = parseInt(parts[1] || "", 10);
    if (
      Number.isNaN(h) ||
      Number.isNaN(m) ||
      h < 0 ||
      h > 23 ||
      m < 0 ||
      m > 59
    ) {
      return out;
    }
    out.scheduleDate = scheduleDate;
    out.scheduleTime = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    out.schedulePrefillAt = schedulePrefillAt;
  }
  return out;
}

function readPrefillFromSessionStorage(): ExtensionHcpPrefill | undefined {
  try {
    const raw = sessionStorage.getItem(EXTENSION_HCP_PREFILL_STORAGE_KEY);
    if (!raw) return undefined;
    return normalizePayload(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

/**
 * Mount once inside `<BrowserRouter>` (above `AppContent`) so listeners run while
 * `AppLoadingGate` is still showing — otherwise /dashboard modals never see postMessage.
 */
export function ExtensionHcpPrefillProvider({ children }: { children: ReactNode }) {
  const [prefill, setPrefill] = useState<ExtensionHcpPrefill | undefined>(() =>
    readPrefillFromSessionStorage()
  );

  const applyIfValid = useCallback((raw: unknown) => {
    const next = normalizePayload(raw);
    if (next) setPrefill(next);
  }, []);

  useLayoutEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isExtensionPrefillAllowedOrigin(event.origin)) return;
      if (event.data?.type !== EXTENSION_HCP_PREFILL_MSG_TYPE) return;
      if (event.data?.source !== EXTENSION_HCP_PREFILL_SOURCE) return;
      applyIfValid(event.data.payload);
    };

    const onDomEvent = () => {
      const next = readPrefillFromSessionStorage();
      if (next) setPrefill(next);
    };

    window.addEventListener("message", onMessage);
    window.addEventListener(EXTENSION_HCP_PREFILL_DOM_EVENT, onDomEvent);

    const next = readPrefillFromSessionStorage();
    if (next) setPrefill(next);

    const delays = [0, 50, 150, 400, 1000, 2500];
    const timers = delays.map((ms) =>
      window.setTimeout(() => {
        const fromStorage = readPrefillFromSessionStorage();
        if (fromStorage) setPrefill(fromStorage);
      }, ms)
    );

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener(EXTENSION_HCP_PREFILL_DOM_EVENT, onDomEvent);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [applyIfValid]);

  return (
    <ExtensionHcpPrefillContext.Provider value={prefill}>
      {children}
    </ExtensionHcpPrefillContext.Provider>
  );
}

export function useExtensionHcpPrefill(): ExtensionHcpPrefill | undefined {
  return useContext(ExtensionHcpPrefillContext);
}
